/**
 * Client-side mirror of the backend ExtensionPackageRestAllowlistCodec
 * (workgroup/src/silos/tableau-server/libraries/tab-domain-packages/src/com/tableausoftware/domain/packages/settings/ExtensionPackageRestAllowlistCodec.java).
 * Parses/serializes the `extensionPackageRestAllowlist` site-setting value -- a JSON array of {url, verb}
 * entries stored as a string -- and validates individual entries for the Site Settings editor.
 *
 * This powers only best-effort, client-side validation feedback in the admin UI. The server-side
 * ExtensionPackageRestAllowlistValidator remains the sole source of truth and rejects any value that does
 * not satisfy these rules on write. Entry urls are relative URL-path templates (not regexes): a
 * leading-'/' path of ordinary URL characters, with optional {slot} placeholders. `{site-id}` and
 * `{api-version}` are filled in automatically at request time; any other `{slot}` must be a
 * resource id supplied by the app. Keep these rules identical to the Java codec.
 */

export interface ExtensionPackageRestAllowlistEntry {
  readonly url: string;
  readonly verb: string;
}

/** HTTP verbs an entry may specify. '*' is legal only as part of the full bypass entry. */
export const EXTENSION_PACKAGE_REST_ALLOWLIST_VERBS: readonly string[] = [
  'GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS',
];

const ALLOWED_VERBS = new Set<string>(EXTENSION_PACKAGE_REST_ALLOWLIST_VERBS);
const WILDCARD = '*';

/** The full bypass entry {"*", "*"}, which allows every request. */
export const EXTENSION_PACKAGE_REST_ALLOWLIST_BYPASS_ENTRY: ExtensionPackageRestAllowlistEntry = { url: WILDCARD, verb: WILDCARD };

// Size limits mirroring the server-side ExtensionPackageRestAllowlistValidator.java, enforced here only for
// inline admin feedback -- the server validator remains the final authority on any persisted value.
export const EXTENSION_PACKAGE_REST_ALLOWLIST_MAX_TOTAL_LENGTH = 32768; // whole serialized value
export const EXTENSION_PACKAGE_REST_ALLOWLIST_MAX_ENTRY_COUNT = 100; // number of entries
export const EXTENSION_PACKAGE_REST_ALLOWLIST_MAX_URL_LENGTH = 512; // a single entry url
// Characters allowed in the literal (non-slot) portion of a path template -- a conservative subset
// of RFC 3986 path characters. Deliberately excludes '*', whitespace, '?', '#' and regex
// metacharacters so entries read as plain URL paths.
const PATH_CHAR = /[A-Za-z0-9/\-._~%:@]/;
// Characters allowed inside a {slot} name.
const SLOT_NAME_CHAR = /[A-Za-z0-9._-]/;

/**
 * Category of validation failure, mapped to a specific user-facing message by the admin UI. (The
 * server-side Java codec returns a prose reason for its InvalidParameterException instead; both
 * apply identical accept/reject rules.)
 */
export type ExtensionPackageRestAllowlistViolation =
  | 'unsupportedVerb'
  | 'notRelative'
  | 'invalidPathChar'
  | 'invalidPlaceholder'
  | 'urlTooLong';

/**
 * Category of list-level (not per-entry) validation failure -- the size limits the server enforces
 * across the whole value. Mapped to a specific user-facing message by the admin UI.
 */
export type ExtensionPackageRestAllowlistListViolation =
  | 'tooManyEntries'
  | 'valueTooLong';

export class ExtensionPackageRestAllowlistCodec {

  private constructor() {}

  /**
   * Parses the stored JSON array into entries. Empty/blank input yields an empty list. Malformed
   * JSON, or JSON that is not an array of {url, verb} objects, also yields an empty list -- the
   * editor starts blank rather than throwing on a corrupt stored value (the server validator
   * guarantees anything actually persisted is well-formed).
   */
  public static parse(value: string | null | undefined): ExtensionPackageRestAllowlistEntry[] {
    if (value == null || value.trim() === '') {
      return [];
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
      .map(entry => ({
        url: typeof entry.url === 'string' ? entry.url : '',
        verb: typeof entry.verb === 'string' ? entry.verb : '',
      }));
  }

  /** Serializes entries into the stored value form: a JSON array of {url, verb} objects. */
  public static serialize(entries: readonly ExtensionPackageRestAllowlistEntry[]): string {
    return JSON.stringify(entries.map(entry => ({ url: entry.url, verb: entry.verb })));
  }

  /** True if `entry` is the full bypass form {"*", "*"}, which allows every request. */
  public static isBypassEntry(entry: ExtensionPackageRestAllowlistEntry): boolean {
    return entry.url === WILDCARD && entry.verb === WILDCARD;
  }

  /**
   * True if the list allows every request -- i.e. any entry is the full bypass entry {"*", "*"}.
   * Mirrors the backend ExtensionPackageRestAllowlistCodec.isBypass(List): a single bypass entry anywhere
   * makes the whole list a bypass. Backs the "allow all" toggle in the admin UI.
   */
  public static isBypassList(entries: readonly ExtensionPackageRestAllowlistEntry[]): boolean {
    return entries.some(ExtensionPackageRestAllowlistCodec.isBypassEntry);
  }

  /**
   * Validates the list as a whole against the server-side size limits (entry count and total
   * serialized length; per-entry url length is covered by findUrlTemplateViolation). Returns the
   * violation category or undefined when within limits. Callers pass the canonical (valid) entries.
   * The backend ExtensionPackageRestAllowlistValidator applies identical limits on write.
   */
  public static findListViolation(
    entries: readonly ExtensionPackageRestAllowlistEntry[]
  ): ExtensionPackageRestAllowlistListViolation | undefined {
    if (entries.length > EXTENSION_PACKAGE_REST_ALLOWLIST_MAX_ENTRY_COUNT) {
      return 'tooManyEntries';
    }
    if (ExtensionPackageRestAllowlistCodec.serialize(entries).length > EXTENSION_PACKAGE_REST_ALLOWLIST_MAX_TOTAL_LENGTH) {
      return 'valueTooLong';
    }
    return undefined;
  }

  /**
   * Validates one entry the way an admin write must satisfy: a known HTTP verb and a valid relative
   * URL-path template -- except the exact bypass entry {"*", "*"}, which is allowed as-is. Returns
   * the violation category (for the UI to map to a specific message) or undefined when valid. The
   * backend ExtensionPackageRestAllowlistCodec.findEntryViolation applies identical accept/reject rules.
   */
  public static findEntryViolation(entry: ExtensionPackageRestAllowlistEntry): ExtensionPackageRestAllowlistViolation | undefined {
    if (ExtensionPackageRestAllowlistCodec.isBypassEntry(entry)) {
      return undefined;
    }
    if (!ALLOWED_VERBS.has(entry.verb.toUpperCase())) {
      return 'unsupportedVerb';
    }
    return ExtensionPackageRestAllowlistCodec.findUrlTemplateViolation(entry.url);
  }

  /**
   * Validates a url as a relative URL-path template: non-empty, starts with '/', only allowed path
   * characters outside of {slots}, and well-formed {slot} placeholders -- balanced braces, no
   * nesting, non-empty, and only allowed slot-name characters inside. Returns the violation category
   * (`notRelative`, `invalidPathChar`, `invalidPlaceholder`) or undefined when valid.
   */
  public static findUrlTemplateViolation(url: string): ExtensionPackageRestAllowlistViolation | undefined {
    if (url === '' || url.charAt(0) !== '/') {
      return 'notRelative';
    }
    if (url.length > EXTENSION_PACKAGE_REST_ALLOWLIST_MAX_URL_LENGTH) {
      return 'urlTooLong';
    }
    let inSlot = false;
    let slotHasContent = false;
    for (let i = 0; i < url.length; i++) {
      const c = url.charAt(i);
      if (c === '{') {
        if (inSlot) {
          return 'invalidPlaceholder'; // nested '{'
        }
        inSlot = true;
        slotHasContent = false;
      } else if (c === '}') {
        if (!inSlot || !slotHasContent) {
          return 'invalidPlaceholder'; // unmatched '}' or empty '{}'
        }
        inSlot = false;
      } else if (inSlot) {
        if (!SLOT_NAME_CHAR.test(c)) {
          return 'invalidPlaceholder'; // bad character in a {slot} name
        }
        slotHasContent = true;
      } else if (!PATH_CHAR.test(c)) {
        return 'invalidPathChar';
      }
    }
    if (inSlot) {
      return 'invalidPlaceholder'; // unmatched '{'
    }
    return undefined;
  }

  /** True if `entry` has no violation (see {@link findEntryViolation}). */
  public static isValidEntry(entry: ExtensionPackageRestAllowlistEntry): boolean {
    return ExtensionPackageRestAllowlistCodec.findEntryViolation(entry) === undefined;
  }
}
